import { Component, OnInit, ChangeDetectionStrategy, Input, Output, EventEmitter, ElementRef, ViewChild, ChangeDetectorRef, HostListener, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { LocaleDto } from '../../../../../../../api/src/creative-unit-package/models/locale.dto';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { debounceTime, takeUntil, Subject } from 'rxjs';
import { MergeTagPickerOverlayService } from '../../merge-tag-picker/merge-tag-picker-overlay.service';
import { MergeTagPickerInputBaseComponent } from '../../merge-tag-picker/merge-tag-picker-input-base/merge-tag-picker-input-base.component';

@Component({
	selector: 'app-text-input',
	templateUrl: './text-input.component.html',
	styleUrls: ['./text-input.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class TextInputComponent extends MergeTagPickerInputBaseComponent implements OnInit, OnChanges, OnDestroy {

	@Input() type: string = 'text';
	@Input() fullWidth: boolean = false;
	@Input() darkMode: boolean = false;
	@Input() prefix: string;
	@Input() suffix: string;
	@Input() suffixActive: boolean;
	@Input() suffixTooltip: string;
	@Input() minLength: number;
	@Input() maxLength: number;
	@Input() showLocale: boolean = false;
	@Input() highlightOnFocus: boolean = false;
	@Input() showWritingTools: boolean = false;
	@Input() showColorPicker: boolean = false;
	@Input() colorPickerDebounceTime: number = 300;
	@Input() includeBrandKitColors: boolean = false;
	@Input() packageId: string;

	@Input() localeData: LocaleDto;
	@Output() localeChange = new EventEmitter<string>();
	@Output() suffixClick = new EventEmitter<any>();
	@ViewChild('textInput', { static: true }) textInput: ElementRef;
	@ViewChild('colorPickerButton', { static: false }) colorPickerButton?: ElementRef<HTMLButtonElement>;
	@ViewChild('colorPickerPanel') set colorPickerPanel(panel: ElementRef<HTMLDivElement> | undefined) {
		this._colorPickerPanel = panel?.nativeElement || undefined;
		if (this._colorPickerPanel && this.isColorPickerOpen) {
			this.attachPanelToSidebar();
		}
	}

	@Input() objectData: Record<string, any>; // Input object for traversal
	@Input() maxSuggestions: number = 10; // Configurable max suggestions
	@Input() truncateLength: number = 20; // Configurable truncation length
	@Input() fuzzyTolerance: number = 0.5; // Configurable fuzzy search tolerance

	suggestions: string[] = []; // Current autocomplete suggestions
	currentSearchTerm: string = ''; // Current search term for highlighting
	fixPlaceholderStyle: boolean = false;

	colorChangeSubject: Subject<string> = new Subject<string>();
	isColorPickerOpen = false;
	private _colorPickerPanel?: HTMLDivElement;
	private outsideClickListener?: (event: MouseEvent) => void;
	private resizeListener?: () => void;
	private scrollListener?: () => void;

	constructor(
		private readonly cdr: ChangeDetectorRef,
		public mergeTagOverlayService: MergeTagPickerOverlayService
	) {
		super(mergeTagOverlayService);
	}

	ngOnChanges(changes: SimpleChanges): void {
		super.ngOnChanges(changes);

		if (changes.showColorPicker?.currentValue === false && this.isColorPickerOpen) {
			this.closeColorPicker();
		}
	}

	ngOnInit() {
		this.fixPlaceholderStyle = (this.placeholder != null && typeof this.placeholder === 'string' && this.placeholder.length > 0)
		&& (this.label == null || typeof this.label !== 'string' || this.label.length <= 0);

		const inputEl: HTMLInputElement = this.textInput.nativeElement;

		if (this.highlightOnFocus) {
			inputEl.addEventListener('focus', this.highlightText);
		}

		inputEl.addEventListener('focus', () => {
			this.showInitialSuggestions();
		});

		super.ngOnInit();

		this.form.valueChanges.pipe(
			takeUntil(this._unsubscribe),
			debounceTime(this.debounceTime)
		).subscribe(value => {
			if (this.objectData) {
				this.onInputChange(value.key);
			}
			this.inputHistory$.next(value.key);
		});

		// Setup debounced color picker
		if (this.showColorPicker) {
			this.colorChangeSubject.pipe(
				debounceTime(this.colorPickerDebounceTime),
				takeUntil(this._unsubscribe)
			).subscribe(color => {
				this.form.patchValue({ [this.controlName]: color });
				this.cdr.markForCheck();
			});
		}
	}

	override getInputElement(): HTMLElement {
		return this.textInput.nativeElement as HTMLElement;
	}

	override getInputWrapperElement(): HTMLElement | null {
		return null;
	}

	override getInputValue(): string {
		return (this.getInputElement() as HTMLInputElement)?.value;
	}

	override ngOnDestroy(): void {
		this.removePanelListeners();
		this._unsubscribe.next();
		this._unsubscribe.complete();

		if (this.highlightOnFocus) {
			this.textInput?.nativeElement?.removeEventListener('focus', this.highlightText);
		}

		super.ngOnDestroy();
	}

	highlightText(event: FocusEvent) {
		(event.target as HTMLInputElement).select();
	}

	showInitialSuggestions() {
		// If we already have a value, show suggestions based on the current value
		// If not, show the first level of properties as suggestions.
		if (this.form.value.key) {
			this.onInputChange(this.form.value.key);
		} else {
			this.currentSearchTerm = '';
			this.suggestions = Object.keys(this.objectData || {});
			this.cdr.detectChanges();
		}
	}

	onInputChange(value: string) {
		if(typeof value?.split !== 'function') return;

		const pathSegments = value?.split('.');
		let currentObject = this.objectData;

		for (let i = 0; i < pathSegments.length - 1; i++) {
			if (currentObject && pathSegments[i] in currentObject) {
				currentObject = currentObject[pathSegments[i]];
			} else {
				this.suggestions = [];
				return;
			}
		}

		const lastSegment = pathSegments[pathSegments.length - 1];
		this.currentSearchTerm = lastSegment;
		this.suggestions = this.getSuggestions(currentObject, lastSegment);
		this.cdr.detectChanges();
	}

	getSuggestions(currentObject: Record<string, any>, input: string): string[] {
		if (!currentObject || typeof currentObject !== 'object') return [];

		const keys = Object.keys(currentObject);
		const filteredKeys = keys.filter(key =>
			key.toLowerCase().includes(input.toLowerCase())
		);

		return filteredKeys.slice(0, this.maxSuggestions);
	}

	getDeepSuggestions(currentObject: Record<string, any>, input: string): string[] {
		if (!currentObject || typeof currentObject !== 'object') return [];

		const results: string[] = [];
		this.searchObject(currentObject, input.toLowerCase(), '', results);

		return results.slice(0, this.maxSuggestions);
	}

	highlightMatch(text: string, input: string): string {
		const regex = new RegExp(`(${input})`, 'gi');
		return text.replace(regex, '<strong>$1</strong>');
	}

	searchObject(obj: Record<string, any>, input: string, currentPath: string, results: string[]) {
		for (const key of Object.keys(obj)) {
			const fullPath = currentPath ? `${currentPath}.${key}` : key;

			if (key.toLowerCase().includes(input)) {
				results.push(fullPath);
			}

			if (typeof obj[key] === 'object' && obj[key] !== null) {
				this.searchObject(obj[key], input, fullPath, results);
			}
		}
	}

	addSuggestionToCurrentValue(suggestion: string) {
		let previousValue = '';
		// Find the most recent value in the history that is different from the current value
		const currentValue = this.form.value.key;
		this.inputHistory$.subscribe(historyValue => {
			if (historyValue !== currentValue) {
				previousValue = historyValue;
			}
		}).unsubscribe();

		let formValue = previousValue?.replace(/<[^>]*>/g, ''); // Remove HTML tags

		if (formValue?.lastIndexOf('.') !== -1) {
			const parentPath = formValue.slice(0, formValue.lastIndexOf('.') + 1);
			this.form.patchValue({
				key: parentPath + suggestion
			});
		} else {
			this.form.patchValue({
				key: suggestion
			});
		}
	}

	onSuggestionSelected(event: MatAutocompleteSelectedEvent) {
		const selectedKey = event.option.value; // Already plain text from the value attribute
		this.addSuggestionToCurrentValue(selectedKey);
	}

	onWritingToolsChange(event: string) {
		this.form.patchValue({
			[this.controlName]: event
		});
		this.emitValueChange(event);
	}

	@HostListener('keydown.enter', ['$event'])
	onEnterKey(event: KeyboardEvent) {
		if (this.suggestions.length > 0) {
			this.addSuggestionToCurrentValue(this.suggestions[0]);
			event.preventDefault();
		}
	}

	@HostListener('document:keydown.escape', ['$event'])
	onEscape(event: KeyboardEvent): void {
		if (!this.isColorPickerOpen) {
			return;
		}

		event.stopPropagation();
		this.closeColorPicker();
	}

	onColorChange(color: string): void {
		this.colorChangeSubject.next(color);
	}

	onBrandKitColorSelected(color: string): void {
		this.form.patchValue({ [this.controlName]: color });
		this.cdr.markForCheck();
	}

	toggleColorPicker(event: MouseEvent): void {
		if (!this.showColorPicker) {
			return;
		}

		event.preventDefault();
		event.stopPropagation();

		this.isColorPickerOpen ? this.closeColorPicker() : this.openColorPicker();
	}

	private openColorPicker(): void {
		this.isColorPickerOpen = true;
		this.cdr.markForCheck();
		// Wait for the panel to render before positioning
		setTimeout(() => this.attachPanelToSidebar());
	}

	private closeColorPicker(): void {
		this.isColorPickerOpen = false;
		this.cdr.markForCheck();
		this.removePanelListeners();
		if (this._colorPickerPanel) {
			this._colorPickerPanel.style.display = 'none';
		}
	}

	private attachPanelToSidebar(): void {
		if (!this.isColorPickerOpen || !this._colorPickerPanel) {
			return;
		}

		const sidebar = this.getSidebarElement();
		const button = this.colorPickerButton?.nativeElement;

		if (!sidebar || !button) {
			console.warn('Unable to position color picker – sidebar or trigger missing', { sidebar, button });
			return;
		}

		if (getComputedStyle(sidebar).position === 'static') {
			sidebar.style.position = 'relative';
		}

		if (!sidebar.contains(this._colorPickerPanel)) {
			sidebar.appendChild(this._colorPickerPanel);
		}

		this._colorPickerPanel.style.position = 'absolute';
		this._colorPickerPanel.style.zIndex = '1200';
		this._colorPickerPanel.style.display = 'block';

		this.positionPanel();
		// Re-measure once the color picker finishes rendering internally
		setTimeout(() => this.positionPanel());

		this.installPanelListeners();
	}

	private installPanelListeners(): void {
		this.removePanelListeners();

		this.outsideClickListener = (event: MouseEvent) => {
			const target = event.target as Node;
			const insidePanel = this._colorPickerPanel?.contains(target);
			const insideButton = this.colorPickerButton?.nativeElement.contains(target);
			if (!insidePanel && !insideButton) {
				this.closeColorPicker();
			}
		};
		document.addEventListener('mousedown', this.outsideClickListener, true);

		this.resizeListener = () => this.positionPanel();
		window.addEventListener('resize', this.resizeListener);

		this.scrollListener = () => this.positionPanel();
		document.addEventListener('scroll', this.scrollListener, true);
	}

	private removePanelListeners(): void {
		if (this.outsideClickListener) {
			document.removeEventListener('mousedown', this.outsideClickListener, true);
			this.outsideClickListener = undefined;
		}

		if (this.resizeListener) {
			window.removeEventListener('resize', this.resizeListener);
			this.resizeListener = undefined;
		}

		if (this.scrollListener) {
			document.removeEventListener('scroll', this.scrollListener, true);
			this.scrollListener = undefined;
		}
	}

	private positionPanel(): void {
		const panel = this._colorPickerPanel;
		const button = this.colorPickerButton?.nativeElement;
		const sidebar = this.getSidebarElement();

		if (!panel || !button || !sidebar) {
			return;
		}

		const sidebarRect = sidebar.getBoundingClientRect();
		const buttonRect = button.getBoundingClientRect();
		const panelWidth = panel.offsetWidth || 230;
		const panelHeight = panel.offsetHeight || 260;
		const gap = 12;

		const rawTop = buttonRect.top - sidebarRect.top;
		const maxTop = Math.max(0, sidebarRect.height - panelHeight - gap);
		const clampedTop = Math.max(0, Math.min(rawTop, maxTop));
		const left = buttonRect.left - sidebarRect.left - panelWidth - gap;

		panel.style.top = `${clampedTop}px`;
		panel.style.left = `${left}px`;
	}

	private getSidebarElement(): HTMLElement {
		const sidebar = document.querySelector('app-ad-editor-sidebar');
		return (sidebar as HTMLElement) || document.body;
	}

	isValidHexColor(color: string): boolean {
		return /^#[0-9A-F]{6}$/i.test(color);
	}

	getColorForPicker(): string {
		const currentValue = this.form.get(this.controlName)?.value;
		return this.isValidHexColor(currentValue) ? currentValue : '#FFFFFF';
	}
}
